import React, { useEffect, useState } from 'react';
import { formatMoney } from '@core/i18n';
import { useActiveGameStore } from '@core/gameengine/state/activeGameStore';
import { useGamingSessionStore } from '@core/regulatory';
import { walletBalanceStyles } from '@core/UI/wallet/WalletBalance.styles';

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function formatElapsedHhMmSs(elapsedMs: number): string {
  const totalSec = Math.max(0, Math.floor(elapsedMs / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}

function formatWallClock(now: Date): string {
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
}

const pillStyles = walletBalanceStyles.header;
const FONT = "'Roboto', sans-serif";

type PillDensity = 'desktop' | 'compact';

const DENSITY: Record<
  PillDensity,
  {
    container: React.CSSProperties;
    amount: React.CSSProperties;
  }
> = {
  desktop: {
    container: {},
    amount: {},
  },
  compact: {
    container: {
      height: '22px',
      padding: '0 6px',
    },
    amount: {
      fontSize: '10px',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
  },
};

const SessionPill: React.FC<{
  value: string;
  valueColor?: string;
  density?: PillDensity;
  'aria-label'?: string;
}> = ({ value, valueColor, density = 'desktop', 'aria-label': ariaLabel }) => {
  const d = DENSITY[density];
  return (
    <div
      aria-label={ariaLabel}
      style={{
        ...pillStyles.container,
        ...d.container,
        fontFamily: FONT,
        flexShrink: 0,
        maxWidth: '100%',
      }}
    >
      <span
        style={{
          ...pillStyles.amount,
          ...d.amount,
          fontFamily: FONT,
          color: valueColor ?? pillStyles.amount.color,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    </div>
  );
};

function useSessionClock(): {
  enabled: boolean;
  active: boolean;
  nowMs: number;
  sessionStartedAtMs: number | null;
} {
  const policy = useGamingSessionStore(s => s.policy);
  const sessionStartedAtMs = useGamingSessionStore(s => s.sessionStartedAtMs);
  const enabled = policy.sessionDisplays;
  const active = enabled && sessionStartedAtMs != null;
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  return { enabled, active, nowMs, sessionStartedAtMs };
}

function usePillDensity(): PillDensity {
  const deviceType = useActiveGameStore((s: any) => s.deviceType);
  return deviceType === 'mobile' || deviceType === 'tablet'
    ? 'compact'
    : 'desktop';
}

/** Wall clock + session elapsed — values only (no labels). Hidden until session starts. */
export const GamingSessionTimers: React.FC<{
  style?: React.CSSProperties;
}> = ({ style }) => {
  const { active, nowMs, sessionStartedAtMs } = useSessionClock();
  const density = usePillDensity();
  if (!active || sessionStartedAtMs == null) return null;

  const elapsedMs = Math.max(0, nowMs - sessionStartedAtMs);

  return (
    <div
      data-skyx-session-timers
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: density === 'compact' ? 4 : 8,
        flexShrink: 1,
        minWidth: 0,
        ...style,
      }}
    >
      <SessionPill
        aria-label="Current time"
        value={formatWallClock(new Date(nowMs))}
        valueColor="#ffffff"
        density={density}
      />
      <SessionPill
        aria-label="Session elapsed"
        value={formatElapsedHhMmSs(elapsedMs)}
        valueColor="#ffffff"
        density={density}
      />
    </div>
  );
};

/** Session net P/L — value only. Hidden until session starts. */
export const GamingSessionNet: React.FC = () => {
  const policy = useGamingSessionStore(s => s.policy);
  const sessionStartedAtMs = useGamingSessionStore(s => s.sessionStartedAtMs);
  const sessionNetPosition = useGamingSessionStore(s => s.sessionNetPosition);
  const currency = useActiveGameStore((s: any) => s.currency ?? 'EUR');
  const density = usePillDensity();

  if (!policy.sessionDisplays || sessionStartedAtMs == null) return null;

  const netValue = formatMoney(sessionNetPosition, currency, {
    showSign: true,
  });
  const netColor = sessionNetPosition > 0 ? '#13B42E' : '#ffffff';

  return (
    <div
      data-skyx-session-net
      style={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <SessionPill
        aria-label="Session net"
        value={netValue}
        valueColor={netColor}
        density={density}
      />
    </div>
  );
};

export const GamingSessionStatus: React.FC = () => (
  <>
    <GamingSessionTimers />
    <GamingSessionNet />
  </>
);
